import { type PowerSyncDrizzleDB, user } from "@stockpilot/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * Every mutation in this file goes through `patchRowVerified`: a raw `UPDATE … WHERE`
 * `id = ?` against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`).
 *
 * ── Why `patchRowVerified` is STILL needed here (updated after the
 * `ThemeProvider` seeding guard) ─────────────────────────────────────────────
 *
 * The server creates the `user` row unconditionally on EVERY signup, whether
 * password or social (`packages/core-rust/src/db/user.rs:101,350` —
 * `INSERT INTO "user" (id, name, email, role[, email_verified])`). Until recently, the
 * `ThemeProvider` seeding `createEffect`
 * (`apps/web/src/providers/theme-provider.tsx`) also did
 * `db.insert(user).values({ id: uid, … })` LOCALLY for ANY `uid` —
 * including real accounts — so that local insert raced the server row during
 * the PowerSync checkpoint window: two writes for the same `id`, one a
 * 3-column ghost row. That is the race the `uid !== VISITOR_ID` guard in `theme-provider.tsx`
 * closes (only the visitor seeds locally now; see
 * `__tests__/theme-provider-seed-window.test.ts` for the measurement and
 * `apps/web/src/providers/__tests__/theme-provider-seed-guard.test.tsx` for
 * the anti-regression test on the real component).
 *
 * That guard does NOT make `patchRowVerified` unnecessary for `user`. The window
 * it protects is wider than local seeding: `db` (the PowerSync accessor)
 * becomes non-null as soon as local SQLite opens, and `userId()` becomes
 * active as soon as the server returns the session — both independent of the
 * PowerSync download stream. Between that and the initial checkpoint bringing
 * back the `user` row the server already created, a window remains in which a
 * normal `UPDATE` (language, theme, blur…) hits a row the local view does not yet
 * have — matches ZERO rows and silently returns "success". This is generic to PowerSync,
 * not a remnant of the already-closed seeding bug: if someone later considers excluding `user` from
 * `patchRowVerified`, it should be because this window is proven
 * not to exist, not because `ThemeProvider` stopped seeding ghosts for real
 * accounts.
 *
 * Scope note: `stock_quant` (`packages/shared/src/inventory/mutations.ts`)
 * has its OWN protection of 7 upserts via `readMatchingRowVerified` against
 * the same class of window (SELECT-before-INSERT-or-UPDATE by
 * product×location×lot). It is an independent defense in a domain unrelated to `user` —
 * the `ThemeProvider` startup guard neither touches nor makes it
 * redundant in any way.
 *
 * Mutations **may throw**: the caller must communicate it, never
 * swallow it.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme) and not through REST: this way
 * the new photo is visible immediately and survives a cold reload without
 * a connection. The BINARY does go through REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; what travels here is only the key returned by that upload.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * The visitor has no own synchronizable row and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
